import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Função auxiliar para formatar data curta
const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }); // Ex: 12/11
};

// Ordem de prioridade para exibição da equipe
const ROLE_ORDER = [
  'Ministro de Música',
  'Violão',
  'Teclado',
  'Baixo',
  'Bateria',
  'Guitarra',
  'Sax',
  'Vocal',
];

function sortTeam(team) {
  if (!team) return [];
  return [...team].sort((a, b) => {
    const indexA = ROLE_ORDER.indexOf(a.role);
    const indexB = ROLE_ORDER.indexOf(b.role);
    const orderA = indexA === -1 ? ROLE_ORDER.length - 1.5 : indexA;
    const orderB = indexB === -1 ? ROLE_ORDER.length - 1.5 : indexB;
    return orderA - orderB;
  });
}

export const generateMonthlyPDF = async (schedules, monthName) => {
  const doc = new jsPDF();

  // === CABEÇALHO ===
  doc.setDrawColor(212, 175, 55); // Dourado
  doc.setLineWidth(0.5);
  doc.line(14, 26, 196, 26);

  // Título
  doc.setTextColor(0, 51, 153);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("ESCALA DE LOUVOR", 105, 14, null, null, "center");

  // Subtítulo
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(`Igreja Bíblica Glória de Cristo - ${monthName}`, 105, 21, null, null, "center");

  // === LOGO ===
  try {
    const imgData = 'img/logo-pdf.png';
    doc.addImage(imgData, 'PNG', 14, 8, 22, 14);
  } catch (error) {
    console.warn("Logo não encontrada ou erro ao carregar imagem no PDF", error);
  }

  // === HELPERS ===
  const getDayOfWeek = (dateString) => {
    const [y, m, d] = dateString.split('-');
    const date = new Date(y, m - 1, d);
    const name = date.toLocaleDateString('pt-BR', { weekday: 'long' });
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  // Ordena os schedules por data e depois por hora
  const sortedSchedules = [...schedules].sort((a, b) => {
    if (a.date !== b.date) {
      return new Date(a.date) - new Date(b.date);
    }
    const timeA = a.time || '00:00';
    const timeB = b.time || '00:00';
    return timeA.localeCompare(timeB);
  });

  // === DIMENSÕES ===
  const BADGE_H = 6;
  const BADGE_GAP = 2;
  const BADGE_RADIUS = 1.5;
  const HEADER_H = 9;
  const CELL_PAD = 4;
  const SECTION_GAP = 5;
  const SONG_BADGE_H = 5.5;
  const SONG_BADGE_GAP = 1.5;
  const ROW_GAP = 4;
  const REPER_LABEL_H = 5;

  const marginL = 14;
  const marginR = 14;
  const fullWidth = 210 - marginL - marginR;
  const contentW = fullWidth - CELL_PAD * 2;

  // Cálculo de largura dinâmica de cada badge
  const getBadgeWidth = (member) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    const nameW = doc.getTextWidth(member.name + ' ');
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.5);
    const roleW = doc.getTextWidth(`(${member.role})`);
    return nameW + roleW + 5;
  };

  // Layout de badges com fluxo horizontal e quebra automática
  const layoutBadges = (team) => {
    if (!team || team.length === 0) return { positions: [], totalRows: 0 };
    let currentX = 0;
    let currentRow = 0;
    const positions = [];

    team.forEach(member => {
      const w = getBadgeWidth(member);
      if (currentX > 0 && currentX + w > contentW) {
        currentRow++;
        currentX = 0;
      }
      positions.push({ x: currentX, row: currentRow, width: w, member });
      currentX += w + BADGE_GAP;
    });

    return { positions, totalRows: currentRow + 1 };
  };

  // Cálculo de largura dinâmica de cada badge do repertório
  const getSongBadgeWidth = (song, index) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    const text = `${index + 1}. ${song.title}${song.artist ? ` - ${song.artist}` : ''}`;
    const textW = doc.getTextWidth(text);
    
    const keyVal = song.customKey || song.key;
    let keyW = 0;
    if (keyVal) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      keyW = doc.getTextWidth(`  [${keyVal}]`);
    }
    
    return textW + keyW + 5;
  };

  // Layout de badges do repertório com fluxo horizontal e quebra automática
  const layoutSongs = (songs) => {
    if (!songs || songs.length === 0) return { positions: [], totalRows: 0 };
    let currentX = 0;
    let currentRow = 0;
    const positions = [];

    songs.forEach((song, index) => {
      const w = getSongBadgeWidth(song, index);
      if (currentX > 0 && currentX + w > contentW) {
        currentRow++;
        currentX = 0;
      }
      positions.push({ x: currentX, row: currentRow, width: w, song, index });
      currentX += w + SONG_BADGE_GAP;
    });

    return { positions, totalRows: currentRow + 1 };
  };

  // === PRÉ-CALCULAR LAYOUTS ===
  const scheduleLayouts = sortedSchedules.map(sched => {
    const sortedTeam = sortTeam(sched.team || []);
    const badgeLayout = layoutBadges(sortedTeam);
    const songs = sched.repertoire || [];
    const songLayout = layoutSongs(songs);

    const badgesH = badgeLayout.totalRows > 0
      ? badgeLayout.totalRows * (BADGE_H + BADGE_GAP) - BADGE_GAP
      : 0;
    const songsH = songLayout.totalRows > 0
      ? songLayout.totalRows * (SONG_BADGE_H + SONG_BADGE_GAP) - SONG_BADGE_GAP
      : 6;
    const totalH = HEADER_H + CELL_PAD + badgesH + SECTION_GAP + REPER_LABEL_H + songsH + CELL_PAD + ROW_GAP;

    return { schedule: sched, sortedTeam, badgeLayout, songLayout, songs, totalH, badgesH, songsH };
  });

  // 1 coluna, 1 linha por culto (conteúdo vazio — renderizado via hooks)
  const bodyData = scheduleLayouts.map(() => ['']);

  autoTable(doc, {
    startY: 32,
    margin: { bottom: 15, left: marginL, right: marginR },
    rowPageBreak: 'avoid',
    showHead: false,
    body: bodyData,
    theme: 'plain',
    styles: {
      cellPadding: 0,
      fontSize: 1,
      textColor: [255, 255, 255],
    },
    columnStyles: { 0: { cellWidth: 'auto' } },

    // Altura de cada célula com base no conteúdo
    didParseCell: (data) => {
      if (data.section === 'body') {
        const layout = scheduleLayouts[data.row.index];
        if (layout) data.cell.styles.minCellHeight = layout.totalH;
      }
    },

    // Renderização customizada de cada card
    didDrawCell: (data) => {
      if (data.section !== 'body') return;
      const layout = scheduleLayouts[data.row.index];
      if (!layout) return;

      const { schedule: sched, badgeLayout, songLayout, songs, badgesH } = layout;
      const cX = data.cell.x;
      const cY = data.cell.y;
      const cW = data.cell.width;
      const cH = data.cell.height - ROW_GAP;

      // ─── 1. FUNDO E BORDA DO CARD ───
      doc.setFillColor(250, 250, 253);
      doc.setDrawColor(215, 220, 230);
      doc.setLineWidth(0.3);
      doc.roundedRect(cX, cY, cW, cH, 2.5, 2.5, 'FD');

      // ─── 2. BARRA DE CABEÇALHO (Dourada) ───
      doc.setFillColor(212, 175, 55);
      doc.roundedRect(cX, cY, cW, HEADER_H, 2.5, 2.5, 'F');
      doc.rect(cX, cY + HEADER_H - 2.5, cW, 2.5, 'F'); // Cantos inferiores retos

      const dayName = getDayOfWeek(sched.date);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0, 51, 153);
      doc.text(
        `${formatDate(sched.date)}   \u2022   ${dayName}   \u2022   ${sched.title}   \u2022   ${sched.time}h`,
        cX + 5, cY + HEADER_H / 2,
        { baseline: 'middle' }
      );

      // ─── 3. BADGES DA EQUIPE ───
      const teamStartY = cY + HEADER_H + CELL_PAD;

      if (badgeLayout.positions.length > 0) {
        badgeLayout.positions.forEach(pos => {
          const bx = cX + CELL_PAD + pos.x;
          const by = teamStartY + pos.row * (BADGE_H + BADGE_GAP);
          const bw = pos.width;
          const { member } = pos;
          const isMin = member.role === 'Ministro de Música';

          // Fundo do badge
          doc.setLineWidth(0.3);
          if (isMin) {
            doc.setFillColor(0, 51, 153);
            doc.setDrawColor(0, 40, 120);
          } else {
            doc.setFillColor(235, 240, 255);
            doc.setDrawColor(190, 200, 225);
          }
          doc.roundedRect(bx, by, bw, BADGE_H, BADGE_RADIUS, BADGE_RADIUS, 'FD');

          // Nome (bold)
          const textY = by + BADGE_H / 2;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(6.5);
          doc.setTextColor(isMin ? 255 : 40, isMin ? 255 : 40, isMin ? 255 : 60);
          doc.text(member.name, bx + 2.5, textY, { baseline: 'middle' });

          // Função (normal, menor)
          const nameW = doc.getTextWidth(member.name + ' ');
          doc.setFont("helvetica", "normal");
          doc.setFontSize(5.5);
          doc.setTextColor(isMin ? 200 : 130, isMin ? 210 : 130, isMin ? 255 : 155);
          doc.text(`(${member.role})`, bx + 2.5 + nameW, textY, { baseline: 'middle' });
        });
      } else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        doc.setTextColor(180, 180, 180);
        doc.text('Sem equipe definida', cX + CELL_PAD, teamStartY + 4);
      }

      // ─── 4. LINHA SEPARADORA ───
      const sepY = teamStartY + badgesH + SECTION_GAP / 2 + 1;
      doc.setDrawColor(210, 215, 225);
      doc.setLineWidth(0.2);
      doc.line(cX + CELL_PAD, sepY, cX + cW - CELL_PAD, sepY);

      // ─── 5. REPERTÓRIO ───
      const reperStartY = teamStartY + badgesH + SECTION_GAP;

      // Label
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(140, 140, 140);
      doc.text("REPERTÓRIO", cX + CELL_PAD, reperStartY + REPER_LABEL_H);

      const songsStartY = reperStartY + REPER_LABEL_H + 1;

      if (songs.length > 0 && songLayout) {
        songLayout.positions.forEach(pos => {
          const sx = cX + CELL_PAD + pos.x;
          const sy = songsStartY + pos.row * (SONG_BADGE_H + SONG_BADGE_GAP);
          const sw = pos.width;
          const { song, index } = pos;

          // Desenha badge quadrado (raio de borda = 0)
          doc.setFillColor(242, 244, 248);
          doc.setDrawColor(210, 215, 225);
          doc.setLineWidth(0.3);
          doc.roundedRect(sx, sy, sw, SONG_BADGE_H, 0, 0, 'FD');

          const textY = sy + SONG_BADGE_H / 2;

          // Título da música
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(60, 60, 60);
          const songText = `${index + 1}. ${song.title}${song.artist ? ` - ${song.artist}` : ''}`;
          doc.text(songText, sx + 2.5, textY, { baseline: 'middle' });

          // Tom em destaque
          const keyVal = song.customKey || song.key;
          if (keyVal) {
            const songTextW = doc.getTextWidth(songText + ' ');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.setTextColor(0, 51, 153);
            doc.text(`[${keyVal}]`, sx + 2.5 + songTextW, textY, { baseline: 'middle' });
          }
        });
      } else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        doc.setTextColor(180, 180, 180);
        doc.text('Músicas ainda não definidas', cX + CELL_PAD + 2, songsStartY + 3);
      }

      // Restaurar estilos
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.1);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
    }
  });

  // === RODAPÉ ===
  const pageCount = doc.internal.getNumberOfPages();
  doc.setFontSize(8);
  doc.setTextColor(150);

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const today = new Date().toLocaleDateString('pt-BR');
    doc.text(`Gerado em ${today} pelo App Escala Louvor`, 105, 290, null, null, 'center');
  }

  // === LÓGICA DE DOWNLOAD/COMPARTILHAMENTO (CORRIGIDA) ===
  const cleanName = `Escala_${monthName.replace('/', '-')}.pdf`.replace(/\s/g, '');

  // Tenta compartilhar (Mobile)
  if (navigator.share && navigator.canShare) {
    try {
      const pdfBlob = doc.output('blob');
      const file = new File([pdfBlob], cleanName, { type: 'application/pdf' });

      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Escala de Louvor',
          text: `Segue a escala de ${monthName}.`,
        });
        return "Compartilhado com sucesso!";
      }
    } catch (error) {
      console.warn("Compartilhamento ignorado ou falhou, tentando download direto...", error);
      // Se der erro no share (ex: usuário cancelou ou desktop não suporta arquivos),
      // o código continua para o fallback abaixo
    }
  }

  // Fallback: Download Direto (Desktop ou Mobile sem suporte a share de arquivos)
  try {
    doc.save(cleanName);
    return "Download iniciado.";
  } catch (err) {
    console.error("Erro crítico ao salvar PDF:", err);
    return "Erro ao gerar arquivo.";
  }
};